/**
 * Single-flight refresh coordinator (03-05 §4).
 *
 * Ported behavior from the proven `sash-admin-panel` interceptors: concurrent
 * 401s share ONE in-flight refresh; everyone waits on the same promise and then
 * retries. A non-network refresh failure means the session is gone → log out; a
 * network/timeout failure does NOT log out (the session may still be alive).
 */

import { ERROR_MESSAGE_KEYS, NormalizedApiError } from '@smartsash/api-client';

export interface RefreshCoordinatorConfig {
  /**
   * Perform the refresh network call. Resolves on success, throws on failure.
   * The caller wires token persistence (bearer) inside this callback so a fresh
   * access token is stored before any waiting request retries.
   */
  performRefresh(): Promise<void>;
  /** Called once when a refresh fails for a non-network reason (session gone). */
  onSessionExpired(): void;
}

export interface RefreshCoordinator {
  /** Refresh the session, joining any in-flight attempt. Rejects if the refresh failed. */
  refresh(): Promise<void>;
}

/**
 * A network/timeout failure — no server verdict reached us, so the session may
 * still be valid. Everything else (401 `session_expired`, 403, 5xx, a rejected
 * envelope) means the refresh was actively denied → the session is gone.
 */
export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof NormalizedApiError &&
    (error.messageKey === ERROR_MESSAGE_KEYS.network ||
      error.messageKey === ERROR_MESSAGE_KEYS.timeout)
  );
}

export function createRefreshCoordinator(config: RefreshCoordinatorConfig): RefreshCoordinator {
  let inFlight: Promise<void> | null = null;

  const run = async (): Promise<void> => {
    try {
      await config.performRefresh();
    } catch (error) {
      if (!isNetworkError(error)) config.onSessionExpired();
      throw error;
    }
  };

  return {
    refresh() {
      inFlight ??= run().finally(() => {
        inFlight = null;
      });
      return inFlight;
    },
  };
}
